import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

interface BrandMarkProps {
  theme?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  showWordmark?: boolean;
  className?: string;
}

const sizeMap = {
  sm: {
    badge: 'h-10 w-10 rounded-2xl',
    dot: 'h-2 w-2',
    wordmark: 'text-lg',
    subline: 'text-[10px]',
  },
  md: {
    badge: 'h-12 w-12 rounded-[20px]',
    dot: 'h-2.5 w-2.5',
    wordmark: 'text-xl',
    subline: 'text-[11px]',
  },
  lg: {
    badge: 'h-14 w-14 rounded-[24px]',
    dot: 'h-3 w-3',
    wordmark: 'text-2xl',
    subline: 'text-xs',
  },
} as const;

export const BrandMark: React.FC<BrandMarkProps> = ({
  theme = 'light',
  size = 'md',
  showWordmark = true,
  className,
}) => {
  const { t } = useTranslation();
  const styles = sizeMap[size];
  const isDark = theme === 'dark';

  return (
    <div className={cn('inline-flex items-center gap-3', className)}>
      <div
        className={cn(
          'relative flex items-center justify-center overflow-hidden shadow-lg',
          styles.badge,
          isDark
            ? 'bg-white/10 ring-1 ring-white/15 backdrop-blur-sm'
            : 'bg-gradient-to-br from-primary via-[#216a5d] to-secondary',
        )}
      >
        <div className="absolute inset-[5px] rounded-[inherit] border border-white/15" />
        <div className="absolute left-3 top-3 h-4 w-1.5 rounded-full bg-white/85" />
        <div className="absolute left-5 top-3 h-1.5 w-4 rounded-full bg-white/80" />
        <div className="absolute bottom-3 right-3 h-1.5 w-5 rounded-full bg-white/65" />
        <div
          className={cn(
            'absolute right-3 top-3 rounded-full bg-[#f7d488]',
            styles.dot,
          )}
        />
        <span className="relative font-display text-sm font-bold uppercase tracking-[0.24em] text-white">
          IB
        </span>
      </div>

      {showWordmark && (
        <div className="leading-none">
          <div
            className={cn(
              'font-display font-bold tracking-[-0.04em]',
              styles.wordmark,
              isDark ? 'text-white' : 'text-slate-900',
            )}
          >
            Inburger<span className="text-secondary">test</span>
          </div>
          <div
            className={cn(
              'mt-1 uppercase tracking-[0.24em]',
              styles.subline,
              isDark ? 'text-white/60' : 'text-slate-500',
            )}
          >
            {t('common.brandSubline')}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandMark;
